/*******************************************************************************
 * Copyright     : Copyright (c)  2025 Honeywell Inc. All rights reserved.
 *                 This work contains trade secrets and confidential materials
 *                 of Honeywell Inc.No part may be reproduced or transmitted in any form by any
 *                 means or for anypurpose without the express written permission of Honeywell. *
 *******************************************************************************/

export interface SoftwareDetailsProps {
  gatewayId?: string;
  gatewayType?: string;
}

//Software Component props
export interface ISoftwareProps extends IBaseSoftwareProps {
  softwares: ISoftwareDetailsProps[];
  config?: IFormData;
}

export interface IBaseSoftwareProps {
  unInstallService?: ((serviceKey: string) => Promise<void>);
  openInstallationForm?: boolean;
  getSoftwareDetails?: ((serviceKey: string, status: string) => Promise<{}>);
  installService?: ((serviceKey: string, formData: IFormData | null) => Promise<void>);
  reinstallService?: ((serviceKey: string, formData: IFormData | null) => Promise<void>);
  fetchServiceLogs?: ((serviceKey: string) => Promise<{ status: number; data: any }>);
  serviceLogs?: string[];
  disableGatewayActions?: boolean;
  showLogsLoader?: boolean;
  refreshTableData?: () => void;
  featureFlags?: { [key: string]: boolean };
  fcAppInstallService?: ((serviceKey: string, formData: IFormData | null) => Promise<void>);
  fcAppUnInstallService?: ((serviceKey: string) => Promise<void>);
  getTargetForgePoints?: ((softwareId: number, softwareKey: string) => void);
  gatewayType?: string;
  getSoftwareVersions?: ((softwareKey: string) => void);
  getAppConfig?: ((softwareKey: string, version: string) => void);
}

export interface ISoftwareDetailsProps {
  softwareId?: number;
  softwareName: string;
  softwareKey: string;
  status: string;
  softwareVersion: string;
  lastAppAction: string;
  menuList?: IActionMenuItemProps[];
  [key: string]: any;
  displayStatus?: string;
  updateAvailable?: boolean;
  images?: IImages;
}

export interface IImageInfo {
  name: string;
  version: string;
  availableVersion?: string;
  primary?: boolean;
}

export interface IImages {
  [key: string]: IImageInfo;
}

export interface IContainerVersionInfo {
  image: string;
  currentVersion: string;
  availableVersion: string;
}

export interface IActionMenuItemProps {
  key: string;
  label: string;
}

export interface IActionComponentProps extends IBaseSoftwareProps {
  menuList?: IActionMenuItemProps[];
  softwareKey: string;
  softwareId?: number;
  softwareName?: string;
  status?: string;
  disableGatewayActions?: boolean;
}

export interface IServiceActionComponentProps extends IActionComponentProps {
  actionKey: string;
  isOpen?: boolean;
  actionTrigger?: number;
}

//Service specific props
//Niagara

export interface IBasicFormProps {
  title: string;
  subTitle?: string;
  cancelEnabled?: boolean;
  config: IFormData;
  columns?: 1 | 2 | 3 | 4;
  submitLabel?: string;
  cancelLabel?: string
  onSubmit?: (formData: IFormData) => void
  onCancel?: (formData?: IFormData) => void
}

export interface NiagaraServiceDetailsProps extends IBasicFormProps {
  softwareId: number;
  softwareKey: string;
  targetForgePoints?: TargetForgePoint[];
  getTargetForgePoints?: ((softwareId: number, softwareKey: string) => void);
  gatewayType?: string;
  fieldProps?: { [key: string]: { disabled?: boolean } };
  showWarningMessage?: boolean;
  notificationProps?: INotificationProps;
}

export interface TargetForgePoint {
  id: string;
  name: string;
}

//Next9
export interface Next9ServiceDetailsProps extends IBasicFormProps {
  serviceId?: string;
  fieldProps?: { [key: string]: IFormFieldProps };
  showWarningMessage?: boolean;
  notificationProps?: INotificationProps;
}

export interface SoftwareVersion {
  version: string;
  id?: string;
}

// Form field props for dynamic forms
export interface IFormFieldProps {
  disabled?: boolean;
  required?: boolean;
  placeholder?: string;
}

// Notification props
export interface INotificationProps {
  title?: string;
  severity?: 'error' | 'warning' | 'info' | 'critical' | 'success';
  detailsText?: string;
}

//View Logs
export interface ServiceLogsProps {
  serviceKey: string;
  serviceLogs?: string[];
  showLogsLoader?: boolean;
  fetchServiceLogs?: (serviceKey: string) => Promise<{ status: number; data: any }>;
}

export interface CustomAppProps extends IBasicFormProps {
  serviceId?: string;
  softwareKey: string;
  versions?: SoftwareVersion[];
  version?: string;
  payload?: any;
  showWarningMessage?: boolean;
  fieldProps?: { [key: string]: IFormFieldProps };
  notificationProps?: INotificationProps;
  getSoftwareVersions?: ((softwareKey: string) => void);
  getAppConfig?: ((softwareKey: string, version: string) => void);
}

//Dynamic Form Models
export enum FormElementType {
  TEXT = 'text',
  PASSWORD = 'password',
  SELECT = 'select',
  INTEGER = 'integer',
  TEXTAREA = 'textarea'
}

export interface IFormElementConfig {
  type: FormElementType;
  id: string;
  label: string;
  placeholder?: string;
  disabled?: boolean;
  options?: Array<{ value: string | number; text: string }>;
  validators?: IValidator[];
  value?: string | number;
}

export interface IValidator {
  type: 'required' | 'minLength' | 'pattern' | 'match' | 'custom';
  message: string;
  value?: number | string | RegExp;
  matchField?: string;
  validate?: (value: string, formData?: IFormData) => string | null;
}

export interface IFormData {
  [key: string]: string | number | undefined;
}

export interface IDynamicFormProps {
  title: string;
  subTitle?: string;
  config: IFormElementConfig[];
  onSubmit: (formData: IFormData) => void;
  onCancel?: () => void;
  submitLabel?: string;
  cancelLabel?: string;
  showWarningMessage?: boolean;
  notificationProps?: INotificationProps
}

//Service specific props
//EBI Cognition
export interface IAvailableSoftwareVersion {
  key: string;
  installedVersion: { version: string; releaseType: string };
  availableVersions: { version: string; size: string; releaseType: string }[];
}

// Software Version Details API Response
export interface ISoftwareVersionComponent {
  component: string;
  version: string;
  description?: string;
}

export interface ISoftwareVersionDetails {
  version: string;
  releaseType: string;
  size: number;
  architectures?: string[];
  releaseNotes?: string;
  components: ISoftwareVersionComponent[];
}

// Software Version Download SAS URL API Response
export interface ISoftwareVersionDownloadChecksum {
  sha256?: string;
}

export interface ISoftwareVersionDownloadArtifact {
  downloadUrl: string;
  metadataDownloadUrl?: string;
  expiresAt?: string;
  fileName: string;
  type?: string;
  version?: string;
  architecture?: string;
  releaseType?: string;
  size?: number;
  sizeFormatted?: string;
  checksum?: ISoftwareVersionDownloadChecksum;
}

export interface ISoftwareVersionDownloadSasUrl
  extends ISoftwareVersionDownloadArtifact {
  files?: ISoftwareVersionDownloadArtifact[];
}

export type ISoftwareVersionDownloadSasUrlResponse =
  | ISoftwareVersionDownloadSasUrl
  | ISoftwareVersionDownloadArtifact[];

// Available Software Versions API Response Models
export interface ICompatibleVersion {
  version: string;
  releaseType?: string;
  size?: number;
  status?: string;
}


export interface IBaseTarVersion {
  version: string;
  status?: string;
  size?: number;
  releaseType?: string;
  releaseDate?: string;
  architectures?: string[];
  compatibleFullIupVersions?: ICompatibleVersion[];
  compatibleIupVersions?: ICompatibleVersion[];
}

export interface IIupTarVersion {
  version: string;
  releaseType?: string;
  incrementalBaseline?: string;
  releaseDate?: string;
  size?: number;
  architectures?: string[];
  compatibleBaseTarVersions?: ICompatibleVersion[];
  compatibleIupVersions?: ICompatibleVersion[];
}

export interface IAvailableSoftwareVersionsApiResponse {
  gatewayType?: string;
  baseTarVersions?: IBaseTarVersion[];
  iupTarVersions?: IIupTarVersion[];
}

// Connected Device Software Version API Response
export interface IConnectedDeviceSoftwareVersionResponse {
  installedVersion?: string;
  baseTarVersion?: string;
  architecture?: string;
  releaseType?: string;
  availableIupTarVersions?: IAvailableIupTarVersion[];
  availableIupVersions?: IAvailableIupTarVersion[];
  availableVersions?: IAvailableIupTarVersion[];
}

export interface IAvailableIupTarVersion {
  version: string;
  releaseType: string;
  size: number;
  architectures?: string[];
  upgradeType?: string;
  compatibility?: {
    incrementalBaseline?: string;
    baseTarVersions?: ICompatibleVersion[];
  };
  policy?: {
    allowRollback?: boolean;
    rollbackToVersions?: string[];
  };
}